/**
 * Session Management Hook
 * Provides session timeout, activity tracking, and security features
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Backend session timeout should be 1 day (86400000 ms)
// Warning shows 5 minutes before timeout
const INACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours (matches backend)
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // 5 minutes warning
const CHECK_INTERVAL = 60 * 1000; // Check every minute

interface UseSessionManagementOptions {
  inactivityTimeout?: number;
  warningTime?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
  enabled?: boolean;
}

export function useSessionManagement(options: UseSessionManagementOptions = {}) {
  const {
    inactivityTimeout = INACTIVITY_TIMEOUT,
    warningTime = WARNING_BEFORE_LOGOUT,
    onWarning,
    onTimeout,
    enabled = true,
  } = options;

  const { signOut, user } = useAuth();
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;

    // Store last activity in sessionStorage
    sessionStorage.setItem('lastActivity', String(Date.now()));
  }, []);

  const checkSession = useCallback(() => {
    if (!enabled || !user) return;

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeUntilTimeout = inactivityTimeout - timeSinceActivity;

    // Show warning
    if (
      timeUntilTimeout <= warningTime &&
      timeUntilTimeout > 0 &&
      !warningShownRef.current
    ) {
      warningShownRef.current = true;
      onWarning?.();
    }

    // Logout due to inactivity
    if (timeSinceActivity >= inactivityTimeout) {
      onTimeout?.();
      signOut();
    }
  }, [enabled, user, inactivityTimeout, warningTime, onWarning, onTimeout, signOut]);

  const extendSession = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  const getTimeRemaining = useCallback((): number => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    return Math.max(0, inactivityTimeout - timeSinceActivity);
  }, [inactivityTimeout]);

  const getTimeRemainingFormatted = useCallback((): string => {
    const ms = getTimeRemaining();
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [getTimeRemaining]);

  useEffect(() => {
    if (!enabled || !user) return;

    // Activity event listeners
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity);
    });

    // Start session check interval
    checkIntervalRef.current = setInterval(checkSession, CHECK_INTERVAL);

    // Check on mount - but validate the stored activity is recent
    // Ignore stale activity from before logout
    const storedActivity = sessionStorage.getItem('lastActivity');
    if (storedActivity) {
      const storedTime = parseInt(storedActivity, 10);
      if (!isNaN(storedTime)) {
        // Only use stored activity if it's from current session (less than 1 hour ago)
        // This prevents stale activity timestamps from before auto-logout
        const timeDifference = Date.now() - storedTime;
        if (timeDifference < 60 * 60 * 1000) { // 1 hour threshold
          lastActivityRef.current = storedTime;
        } else {
          // Stale activity - reset to now
          lastActivityRef.current = Date.now();
          sessionStorage.setItem('lastActivity', String(Date.now()));
        }
      }
    } else {
      // No stored activity - reset to now
      lastActivityRef.current = Date.now();
      sessionStorage.setItem('lastActivity', String(Date.now()));
    }

    checkSession();

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [enabled, user, updateActivity, checkSession]);

  return {
    extendSession,
    getTimeRemaining,
    getTimeRemainingFormatted,
    isActive: enabled && !!user,
  };
}
